import {
  QuestionGroupStatus,
  QuestionGroupType,
  useGetQuestionGroupByIdQuery,
  usePublishItemSetMutation,
  useSaveItemSetAsUnpublishedMutation,
  useSaveItemSetAsDraftMutation,
  usePendingItemSetMutation,
  useDisableItemSetMutation,
  useDeleteItemSetSubQuestionMutation,
  type PublishItemSetMutationOptions,
  type SaveItemSetAsDraftMutationOptions,
  type SaveItemSetAsUnpublishedMutationOptions,
  type PendingItemSetMutationOptions,
  type DisableItemSetMutationOptions,
  type DeleteItemSetSubQuestionMutationOptions,
} from '@exam-management/graphql';
import { Navigate } from '@exam-management/router';
import { Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import type { ItemSet, PublishValidationError } from './types';
import { transformItemSetPublicationErrors } from './utils';

interface ItemSetProviderProps extends PropsWithChildren {
  itemSetId: string;
}

interface SaveItemSetInput {
  name: string;
  paragraph: string;
}

type SaveItemSetOptions =
  | SaveItemSetAsDraftMutationOptions
  | SaveItemSetAsUnpublishedMutationOptions
  | undefined;

interface ItemSetContextData {
  itemSet: ItemSet;
  isPublishing: boolean;
  publish: (options?: PublishItemSetMutationOptions) => Promise<void>;
  publicationErrors: PublishValidationError[];
  isUpdatingToPending: boolean;
  pending: (options?: PendingItemSetMutationOptions) => Promise<void>;
  isDisabling: boolean;
  disable: (options?: DisableItemSetMutationOptions) => Promise<void>;
  save: (data: SaveItemSetInput, options?: SaveItemSetOptions) => Promise<void>;
  isDeleting: boolean;
  deleteSubQuestion: (
    subQuestionId: string,
    options?: DeleteItemSetSubQuestionMutationOptions,
  ) => Promise<void>;
}

const DEFAULT_ITEM_SET_INFORMATION: ItemSet = {
  id: '',
  name: '',
  refCode: '',
  questions: [],
  metadata: {
    paragraph: '',
  },
  type: QuestionGroupType.ItemSet,
  questionStructures: [],
  status: QuestionGroupStatus.Draft,
  updatedAt: new Date(),
  totalQuestions: 0,
};

const ItemSetContext = createContext<ItemSetContextData | undefined>(undefined);

function ItemSetProvider(props: Readonly<ItemSetProviderProps>) {
  const { itemSetId, children } = props;

  const { data: getQuestionResult, loading: isLoading } = useGetQuestionGroupByIdQuery({
    variables: {
      questionGroupId: itemSetId,
    },
  });

  const [
    publishItemSetMutation,
    { loading: isPublishing, reset: resetPublicationResult, error: publicationErrorsResult },
  ] = usePublishItemSetMutation();

  const publicationErrors = useMemo(
    () => transformItemSetPublicationErrors(publicationErrorsResult),
    [publicationErrorsResult],
  );

  const publish = async (options: PublishItemSetMutationOptions | undefined = {}) => {
    if (!getQuestionResult?.qctGetQuestionGroupById) {
      return;
    }

    resetPublicationResult();
    await publishItemSetMutation({
      variables: {
        id: getQuestionResult.qctGetQuestionGroupById.id,
      },
      ...options,
    });
  };

  const [pendingItemSetMutation, { loading: isUpdatingToPending }] = usePendingItemSetMutation();

  const pending = async (options: PendingItemSetMutationOptions | undefined = {}) => {
    if (!getQuestionResult?.qctGetQuestionGroupById) {
      return;
    }

    await pendingItemSetMutation({
      variables: {
        id: getQuestionResult.qctGetQuestionGroupById.id,
      },
      ...options,
    });
  };

  const [deleteItemSetSubQuestionMutation, { loading: isDeleting }] =
    useDeleteItemSetSubQuestionMutation();

  const deleteSubQuestion = async (
    subQuestionId: string,
    options: DeleteItemSetSubQuestionMutationOptions | undefined = {},
  ) => {
    const itemSetId = getQuestionResult?.qctGetQuestionGroupById.id;

    if (!itemSetId) return;

    await deleteItemSetSubQuestionMutation({
      variables: {
        input: {
          itemSetId,
          subQuestionId,
        },
      },
      ...options,
    });
  };

  const [disableItemSetMutation, { loading: isDisabling }] = useDisableItemSetMutation();

  const disable = async (options: DisableItemSetMutationOptions | undefined = {}) => {
    if (!getQuestionResult?.qctGetQuestionGroupById) {
      return;
    }

    await disableItemSetMutation({
      variables: {
        id: getQuestionResult.qctGetQuestionGroupById.id,
      },
      ...options,
    });
  };

  const [saveItemSetAsDraft] = useSaveItemSetAsDraftMutation();
  const [saveItemSetAsUnpublished] = useSaveItemSetAsUnpublishedMutation();

  const save = async (data: SaveItemSetInput, options: SaveItemSetOptions | undefined = {}) => {
    if (!getQuestionResult?.qctGetQuestionGroupById) {
      return;
    }

    const { id, status } = getQuestionResult.qctGetQuestionGroupById;

    resetPublicationResult();

    switch (status) {
      case QuestionGroupStatus.Draft:
      case QuestionGroupStatus.PreDraft:
        await saveItemSetAsDraft({
          variables: {
            input: { ...data, questionGroupId: id },
          },
          ...(options as SaveItemSetAsDraftMutationOptions),
        });
        break;
      case QuestionGroupStatus.Unpublished:
        await saveItemSetAsUnpublished({
          variables: {
            input: { ...data, id },
          },
          ...(options as SaveItemSetAsUnpublishedMutationOptions),
        });
        break;
      default:
        console.error('Invalid status', status);
    }
  };

  const contextValue = useMemo(
    () => ({
      itemSet: getQuestionResult?.qctGetQuestionGroupById ?? DEFAULT_ITEM_SET_INFORMATION,
      isPublishing,
      publish,
      publicationErrors,
      isUpdatingToPending,
      pending,
      isDisabling,
      disable,
      save,
      isDeleting,
      deleteSubQuestion,
    }),
    [
      isPublishing,
      getQuestionResult?.qctGetQuestionGroupById,
      publish,
      publicationErrors,
      isUpdatingToPending,
      pending,
      isDisabling,
      disable,
      save,
      isDeleting,
      deleteSubQuestion,
    ],
  );

  if (isLoading) {
    return <Loading height='100%' />;
  }

  if (!getQuestionResult?.qctGetQuestionGroupById) {
    // TODO: Navigate to error page instead
    return <Navigate to='/qbank-structures' />;
  }

  return (
    <ItemSetContext.Provider value={contextValue}>{children ?? <Outlet />}</ItemSetContext.Provider>
  );
}

export { ItemSetContext, ItemSetProvider };
